import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * KT Milano permanent platform banner (never "sponsored").
 * Size variants:
 *   - "small"   : compact banner (fits below content, mobile-first)
 *   - "large"   : full identity block for end-of-page (result / plan / salon PDF)
 *   - "email"   : returns HTML string for email templates (server-side use).
 *
 * Palette: white/#F3F3F3 background, champagne/olive accents. Never heavy/black.
 */
export default function KTBanner({ size = "small", lang = "en", salonSlug = "", refParam = "hairtu_salon" }) {
  const [b, setB] = useState(null);
  useEffect(() => {
    axios.get(`${API}/branding`).then((r) => setB(r.data)).catch(() => {});
  }, []);
  if (!b) return null;

  const base = b.website_url || "https://www.ktmilano.com";
  const url = salonSlug
    ? `${base}${base.includes("?") ? "&" : "?"}ref=${refParam}_${salonSlug}`
    : base;
  const tagline = lang === "it" ? b.tagline_it : b.tagline_en;
  const cta = lang === "it" ? b.discover_cta_it : b.discover_cta_en;
  const hairtu = lang === "it" ? b.hairtu_line_it : b.hairtu_line_en;

  if (size === "large") {
    return (
      <div
        data-testid="kt-banner-large"
        className="rounded-3xl border kt-line kt-shadow overflow-hidden grid sm:grid-cols-[1fr,auto] items-center"
        style={{ backgroundColor: b.banner_bg || "#FFFFFF" }}
      >
        <div className="p-8">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2" data-testid="kt-hairtu-line">
            {hairtu}
          </div>
          <div className="flex items-center gap-3 mb-3">
            {b.logo_url && (
              <img src={b.logo_url} alt="KT Milano" loading="lazy" decoding="async" data-testid="kt-logo"
                className="h-8 w-auto object-contain" />
            )}
            <h3 className="font-serif text-2xl text-[#383633] tracking-tight">{b.name}</h3>
          </div>
          <p className="text-[#6F6B66] mb-2">{tagline}</p>
          <a
            href={url}
            target="_blank" rel="noreferrer"
            data-testid="kt-discover-cta-large"
            className="inline-flex items-center gap-2 rounded-full text-[#383633] border border-[#383633] hover:bg-[#F8F6F2] px-5 py-2.5 text-[11px] kt-tracking uppercase transition"
            style={{ borderColor: "#383633" }}
          >
            {cta} <span aria-hidden>→</span>
          </a>
          <div className="text-[10px] text-[#6F6B66] mt-3">
            <a href={url} target="_blank" rel="noreferrer" className="hover:text-[#383633] underline decoration-[#D8C4A5] underline-offset-4">
              www.ktmilano.com
            </a>
          </div>
        </div>
        {b.product_image_url && (
          <div className="hidden sm:block w-56 h-40 shrink-0 overflow-hidden"
               style={{ backgroundColor: "#F8F6F2" }}>
            <img src={b.product_image_url} alt="" loading="lazy" decoding="async" data-testid="kt-product-image"
              className="w-full h-full object-cover" />
          </div>
        )}
      </div>
    );
  }

  // "small" — premium horizontal strip
  return (
    <a
      href={url}
      target="_blank" rel="noreferrer"
      data-testid="kt-banner-small"
      className="block rounded-2xl border kt-line overflow-hidden group transition kt-shadow"
      style={{ backgroundColor: b.banner_bg || "#F3F3F3" }}
    >
      <div className="flex items-center gap-4 px-4 py-3 sm:py-3.5">
        {b.logo_url ? (
          <img src={b.logo_url} alt="KT Milano" loading="lazy" decoding="async" className="h-6 w-auto object-contain shrink-0" />
        ) : (
          <span className="text-xs kt-tracking-wider uppercase text-[#383633] shrink-0">
            {b.name}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] truncate">
            {hairtu}
          </div>
          <div className="text-sm text-[#383633] truncate">{tagline}</div>
        </div>
        <span
          className="hidden sm:inline-flex items-center gap-1.5 rounded-full text-[10px] kt-tracking uppercase px-3 py-1.5 border transition group-hover:bg-white shrink-0"
          style={{ borderColor: b.banner_accent || "#D8C4A5", color: "#383633" }}
        >
          {cta} <span aria-hidden>→</span>
        </span>
      </div>
    </a>
  );
}
